import { AutoformatPlugin } from "@platejs/autoformat";
import {
	BoldPlugin,
	CodePlugin,
	HighlightPlugin,
	ItalicPlugin,
	StrikethroughPlugin,
	SubscriptPlugin,
	SuperscriptPlugin,
	UnderlinePlugin,
} from "@platejs/basic-nodes/react";
import { CalloutPlugin } from "@platejs/callout/react";
import { CodeBlockPlugin, CodeLinePlugin, CodeSyntaxPlugin } from "@platejs/code-block/react";
import { IndentPlugin } from "@platejs/indent/react";
import { LinkPlugin } from "@platejs/link/react";
import { ListPlugin } from "@platejs/list/react";
import { MarkdownPlugin } from "@platejs/markdown";
import { ImagePlugin } from "@platejs/media/react";
import { SlashInputPlugin, SlashPlugin } from "@platejs/slash-command/react";
import {
	TableCellHeaderPlugin,
	TableCellPlugin,
	TablePlugin,
	TableRowPlugin,
} from "@platejs/table/react";
import { TocPlugin } from "@platejs/toc/react";
import { TogglePlugin } from "@platejs/toggle/react";
import {
	type AnyPluginConfig,
	ExitBreakPlugin,
	KEYS,
	type Path,
	TrailingBlockPlugin,
} from "platejs";
import { BlockPlaceholderPlugin, createPlatePlugin } from "platejs/react";
import { BlockquoteElement } from "../nodes/blockquote-node";
import { CalloutElement } from "../nodes/callout-node";
import { CodeBlockElement, CodeLineElement, CodeSyntaxLeaf } from "../nodes/code-block-node";
import { HeadingElement } from "../nodes/heading-node";
import { HrElement } from "../nodes/hr-node";
import { ImageElement } from "../nodes/image-node";
import { LinkElement } from "../nodes/link-node";
import { SlashInputElement } from "../nodes/slash-node";
import {
	TableCellElement,
	TableCellHeaderElement,
	TableElement,
	TableRowElement,
} from "../nodes/table-node";
import { TocElement } from "../nodes/toc-node";
import { ToggleElement } from "../nodes/toggle-node";
import { FixedToolbar } from "../ui/fixed-toolbar";
import { FixedToolbarButtons } from "../ui/fixed-toolbar-buttons";
import { FloatingToolbar } from "../ui/floating-toolbar";
import { FloatingToolbarButtons } from "../ui/floating-toolbar-buttons";

// --- Autoformat rules ---

const autoformatRules = [
	// Marks
	{ match: { end: "**", start: "**" }, mode: "mark" as const, type: KEYS.bold },
	{
		match: { end: "__", start: "__" },
		mode: "mark" as const,
		type: KEYS.underline,
	},
	{ match: { end: "*", start: "*" }, mode: "mark" as const, type: KEYS.italic },
	{ match: { end: "_", start: "_" }, mode: "mark" as const, type: KEYS.italic },
	{
		match: { end: "~~", start: "~~" },
		mode: "mark" as const,
		type: KEYS.strikethrough,
	},
	{
		match: { end: "==", start: "==" },
		mode: "mark" as const,
		type: KEYS.highlight,
	},
	{ match: { end: "`", start: "`" }, mode: "mark" as const, type: KEYS.code },
	// Blocks
	{ match: "# ", mode: "block" as const, type: KEYS.h1 },
	{ match: "## ", mode: "block" as const, type: KEYS.h2 },
	{ match: "### ", mode: "block" as const, type: KEYS.h3 },
	{ match: "#### ", mode: "block" as const, type: KEYS.h4 },
	{ match: "##### ", mode: "block" as const, type: KEYS.h5 },
	{ match: "###### ", mode: "block" as const, type: KEYS.h6 },
	{ match: "> ", mode: "block" as const, type: KEYS.blockquote },
	{ match: "---", mode: "block" as const, type: KEYS.hr },
	{ match: "***", mode: "block" as const, type: KEYS.hr },
];

// --- Plugin Assembly ---

export const WikiEditorKit: AnyPluginConfig[] = [
	// === Elements ===

	// Headings (h1-h6)
	createPlatePlugin({ key: KEYS.h1 }).withComponent(HeadingElement),
	createPlatePlugin({ key: KEYS.h2 }).withComponent(HeadingElement),
	createPlatePlugin({ key: KEYS.h3 }).withComponent(HeadingElement),
	createPlatePlugin({ key: KEYS.h4 }).withComponent(HeadingElement),
	createPlatePlugin({ key: KEYS.h5 }).withComponent(HeadingElement),
	createPlatePlugin({ key: KEYS.h6 }).withComponent(HeadingElement),

	// Blockquote
	createPlatePlugin({ key: KEYS.blockquote }).withComponent(BlockquoteElement),

	// Horizontal rule
	createPlatePlugin({
		key: KEYS.hr,
		node: { isElement: true, isVoid: true },
	}).withComponent(HrElement),

	// Code block
	CodeBlockPlugin.withComponent(CodeBlockElement),
	CodeLinePlugin.withComponent(CodeLineElement),
	CodeSyntaxPlugin.withComponent(CodeSyntaxLeaf),

	// Table
	TablePlugin.withComponent(TableElement),
	TableRowPlugin.withComponent(TableRowElement),
	TableCellPlugin.withComponent(TableCellElement),
	TableCellHeaderPlugin.withComponent(TableCellHeaderElement),

	// Toggle
	TogglePlugin.withComponent(ToggleElement),

	// Callout
	CalloutPlugin.withComponent(CalloutElement),

	// Link
	LinkPlugin.configure({
		render: {
			node: LinkElement,
		},
	}),

	// TOC
	TocPlugin.configure({
		options: { topOffset: 80 },
	}).withComponent(TocElement),

	// Image (basic, no upload)
	ImagePlugin.configure({
		options: { disableUploadInsert: true },
	}).withComponent(ImageElement),

	// === Marks ===
	BoldPlugin,
	ItalicPlugin,
	UnderlinePlugin,
	StrikethroughPlugin,
	CodePlugin,
	HighlightPlugin,
	SubscriptPlugin,
	SuperscriptPlugin,

	// === Block Style ===
	IndentPlugin.configure({
		inject: {
			targetPlugins: [
				...KEYS.heading,
				KEYS.p,
				KEYS.blockquote,
				KEYS.codeBlock,
				KEYS.toggle,
				KEYS.img,
			],
		},
		options: { offset: 24 },
	}),

	ListPlugin,

	// === Editing ===
	SlashPlugin,
	SlashInputPlugin.withComponent(SlashInputElement),

	AutoformatPlugin.configure({
		options: {
			enableUndoOnDelete: true,
			rules: autoformatRules,
		},
	}),

	ExitBreakPlugin.configure({
		options: {
			rules: [{ hotkey: "mod+enter" }, { before: true, hotkey: "mod+shift+enter" }],
		},
	}),

	TrailingBlockPlugin,

	BlockPlaceholderPlugin.configure({
		options: {
			className:
				"before:absolute before:cursor-text before:text-muted-foreground/80 before:content-[attr(placeholder)]",
			placeholders: {
				[KEYS.p]: "Type '/' for commands...",
			},
			query: ({ path }: { path: Path }) => path.length === 1,
		},
	}),

	// === Parsers ===
	MarkdownPlugin,

	// === UI: Toolbars ===
	createPlatePlugin({
		key: "fixed-toolbar",
		render: {
			beforeEditable: () => (
				<FixedToolbar>
					<FixedToolbarButtons />
				</FixedToolbar>
			),
		},
	}),
	createPlatePlugin({
		key: "floating-toolbar",
		render: {
			afterEditable: () => (
				<FloatingToolbar>
					<FloatingToolbarButtons />
				</FloatingToolbar>
			),
		},
	}),
];
